import type { ContentRefKind } from '@gitbook/app-content-refs';
import type { ContentReferenceContext } from '@gitbook/app-database';
import { contentRef } from '@gitbook/doc-core';
import type { HiveClient } from '@gitbook/hive-client';
import { PalettePositionMaxHeight, PalettePositionWidth } from '@gitbook/react-uikit';
import { isBlockRangeSelection, type TextRange, type Value } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { isUrlLike } from '@gitbook/util-url';

import { createInputContentRefPalette } from '../../ContentReferences';

/**
 * Content refreferences that are allowed to be inserted into an inline link.
 */
export const ALLOWED_CONTENT_REFS_IN_INLINE_LINK: ContentRefKind[] = [
    'url',
    'anchor',
    'page',
    'space',
];

/**
 * Create a palete to insert a link content reference.
 */
export function createLinkPalette(args: {
    editor: EditorContainer;
    hiveClient: HiveClient;
    contentReferenceContext: ContentReferenceContext;
    getRange?: () => TextRange;
}) {
    const { editor, hiveClient, contentReferenceContext, getRange } = args;
    return createInputContentRefPalette(
        (ref) => {
            editor.change((change) => {
                const textRange = getRange ? getRange() : change.value.selection;

                if (isBlockRangeSelection(textRange)) {
                    return change;
                }

                contentRef.insertContentReferenceAtRange(change, ref, textRange);
            });
        },
        {
            ...contentReferenceContext,
            document: editor.value.document,
        },
        hiveClient,
        {
            ...(isSelectionUrlLike(editor.value)
                ? {
                      search: {
                          initialValue: editor.value.fragment.getText(),
                      },
                  }
                : {}),
            position: {
                preferredPlacement: 'left',
                preferredAlignment: 'middle',
                width: PalettePositionWidth.LINKS,
                maxHeight: PalettePositionMaxHeight.AUTO,
            },
            accept: ALLOWED_CONTENT_REFS_IN_INLINE_LINK,
        }
    );
}

/**
 * Evaluate whether the currently selected text looks like a URL.
 */
function isSelectionUrlLike(value: Value): boolean {
    if (isBlockRangeSelection(value.selection) || value.isCollapsed) {
        return false;
    }

    const text = value.fragment.getText();

    if (!text) {
        return false;
    }

    return isUrlLike(text);
}
